import transformLogs from './utils/transformLogs';

// https://cloud-wave.atlassian.net/wiki/spaces/PROD/pages/1603043329/Logs

// Needed to somehow get a unique type that is technically a string but cannot be replicated by using a string directly.
// Basically forcing us to use the LogEvent object to pass messages instead of being able to pass string directly to the function.

type Distinct<T, DistinctName> = T & { __TYPE__: DistinctName };

export type TLogEventValue = Distinct<string, 'TLogEventValue'>;

const LogEvent = '' as TLogEventValue;

const ModalActions = () => ({
  OPEN: LogEvent,
  CLOSE: LogEvent,
  SUCCESS: LogEvent,
  FAIL: LogEvent,
});

const SuccessFail = () => ({
  SUCCESS: LogEvent,
  FAIL: LogEvent,
});

const CUD = () => ({
  CREATE: SuccessFail(),
  UPDATE: SuccessFail(),
  DELETE: SuccessFail(),
});

const LogEventsMap = {
  AGENT_TASK: {
    CREATE: ModalActions(),
    FETCH_AGENTS: SuccessFail(),
  },
  OUTBOUND_SMS_SEND: ModalActions(),

  INIT_CONNECT_STREAMS_ERROR: LogEvent,
  CONNECT_SOFTPHONE_ERROR: LogEvent,
  INVALID_VDI_PARAMETER: LogEvent,

  TASK: {
    INCOMING: LogEvent,
    OUTBOUND: LogEvent,
    MISSED: LogEvent,
    ACCEPT: SuccessFail(),
    TRANSFER_TO_QUEUE: SuccessFail(),
    CONNECT: SuccessFail(),
    REJECT: SuccessFail(),
    CLEAR: SuccessFail(),
    DESTROY: SuccessFail(),
    CLOSED: SuccessFail(),
    PAUSE: SuccessFail(),
    RESUME: SuccessFail(),
    CONTACT_ATTRIBUTE_SET: SuccessFail(),
  },

  PREVIEW_DIALING_TASK: {
    IS_PREVIEW_MODE_ERROR: LogEvent,
    GET_PREVIEW_CONFIGURATION_ERROR: LogEvent,
  },

  CONNECT_ACTIONS: {
    ERROR: LogEvent,
  },

  LOGGING: {
    PRUNING_LOGS_FAILED: LogEvent,
    LOG_UPLOAD_FAILED: LogEvent,
    LOG_STREAM_CREATION_FAILED: LogEvent,
  },

  CALL_RECORDING: {
    PAUSE: SuccessFail(),
    RESUME: SuccessFail(),
    STOP: SuccessFail(),
  },

  CALL_ACTIONS: {
    HOLD: SuccessFail(),
    RESUME: SuccessFail(),
    MUTE: SuccessFail(),
    UNMUTE: SuccessFail(),
  },

  EXTERNAL_TRANSFER: {
    INITIATED: SuccessFail(),
    COMPLETED: SuccessFail(),
    FAILED: LogEvent,
    ENDPOINT: SuccessFail(),
    ATTRIBUTE_SET: SuccessFail(),
  },

  CONFERENCE_ACTIONS: {
    HOLD: SuccessFail(),
    RESUME: SuccessFail(),
    END: SuccessFail(),
  },

  OUTBOUND_CALL: {
    QUEUED: LogEvent,
    INITIATED: SuccessFail(),
    RELATED_CALLS_UNLINKED: LogEvent,
  },

  AFTER_CALL_WORK: {
    SKIPPED: LogEvent,
    SUBMITTED: SuccessFail(),
    FETCHING_TRANSCRIPTS: SuccessFail(),
    GENERATE_NOTES: SuccessFail(),
  },

  INTERACTION: {
    CREATE: SuccessFail(),
  },

  EMAIL: {
    DRAFT_CREATED: ModalActions(),
    REPLY_ALL_CREATED: LogEvent,
    REPLY_CREATED: LogEvent,
    FORWARD_CREATED: LogEvent,
    SENT: SuccessFail(),
    CONTENT_LOAD_FAILED: LogEvent,
    IMAGE_RESIZE_FAILED: LogEvent,
    PAUSED: LogEvent,
    RESUMED: LogEvent,
  },

  AGENT_STATUS_CHANGED: {
    BY_CONNECT: LogEvent,
    MANUALLY: SuccessFail(),
  },

  STREAMS_INIT: SuccessFail(),

  AUTH: {
    SIGN_IN: SuccessFail(),
    SIGN_OUT: {
      INITIATED: LogEvent,
      COMPLETED: SuccessFail(),
    },
    AUTH_STEP: LogEvent,
    SESSION_EXPIRED: LogEvent,
    CHECK_USER_SESSION: SuccessFail(),
    REFRESH_TOKEN: SuccessFail(),
    FETCH_USER_ERROR: LogEvent,
    FORGET_DEVICE: SuccessFail(),
    AGENT_CONFIG: SuccessFail(),
    INITIALIZE_USER: SuccessFail(),
    REFRESH_TOKEN_FAIL: LogEvent,
    REFRESH_TOKEN_RACE: LogEvent,
  },

  PAGE_RELOAD_WITH_TASKS: LogEvent,

  CUSTOMER_PROFILE: CUD(),

  CONTACT: {
    PERSONAL: CUD(),
    COMPANY: CUD(),
    LOAD: SuccessFail(),
    REFRESH: SuccessFail(),
  },

  WIDGET_LOAD_FAILED: LogEvent,
  REPORT_LOAD_FAILED: LogEvent,
  QUEUES_LOAD_FAILED: LogEvent,
  INTERACTION_HISTORY_LOAD_FAILED: LogEvent,
  MEDIA_DEVICES_LOAD_FAILED: LogEvent,
  EXTERNAL_SCRIPT_LOAD_FAILED: LogEvent,
  ATTACHMENT_UPLOAD_FAILED: LogEvent,
  TRANSFER_ENDPOINT_LOAD_FAILED: LogEvent,
  AGENT_STATUS_UPDATE_FAILED: LogEvent,
  FETCH_USERS_FAILED: LogEvent,
  AGENT_OFFLINE: LogEvent,

  PERFORMANCE: {
    HEARTBEAT: SuccessFail(),
    ACK_TIMEOUT: LogEvent,
  },

  TASK_EVENT: {
    DROPPED: LogEvent,
    PROCESSED: LogEvent,
  },

  AGENT_MANAGEMENT: CUD(),

  ACGR_CONFIGURATION_ERROR: LogEvent,

  UNCAUGHT_EXCEPTION: LogEvent,

  DEFAULT: LogEvent,
};

export type TLogEvents = typeof LogEventsMap;

const LogEvents: TLogEvents = transformLogs(LogEventsMap);

export default LogEvents;
